// M5 Smile Hold — hold a smile for 5 cumulative seconds within 8s.
// Spec: /microgames/05-smile-hold.md
// Smile detection lifted from chomp-ios MouthTracker.swift (face.smiling).

import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
} from '@pose-game/core';

const DURATION_SEC = 12;
const REQUIRED_HOLD_SEC = 4;
/** Consecutive seconds of NOT-smiling that nukes progress back to 0. */
const BREAK_GRACE_SEC = 0.5;
/** Smile fill rate (progress 0..1 per second of smile). */
const FILL_RATE = 1 / REQUIRED_HOLD_SEC;
/** Slow drain when not smiling but within grace window. */
const DRAIN_RATE = 0.1;

interface State {
  elapsed: number;
  progress: number; // 0..1
  notSmilingFor: number; // consecutive seconds with smiling === false
  smiling: boolean;
  finished: boolean;
  won: boolean;
  totalSmileSec: number; // cumulative real smile time (for detail/reporting)
  gameCtx: GameContext | null;
  /** Frame-local shake phase, advanced in tick(); used by render. */
  shakePhase: number;
}

const initialState = (): State => ({
  elapsed: 0,
  progress: 0,
  notSmilingFor: 0,
  smiling: false,
  finished: false,
  won: false,
  totalSmileSec: 0,
  gameCtx: null,
  shakePhase: 0,
});

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

const lerpColor = (
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): string => {
  const r = Math.round(lerp(a[0], b[0], t));
  const g = Math.round(lerp(a[1], b[1], t));
  const bl = Math.round(lerp(a[2], b[2], t));
  return `rgb(${r}, ${g}, ${bl})`;
};

export const smileHold: Microgame = (() => {
  let state = initialState();

  const init = (ctx: GameContext): void => {
    state = initialState();
    state.gameCtx = ctx;
  };

  const tick = (dt: number, frame: InputFrame): void => {
    if (state.finished) return;

    state.elapsed += dt;
    state.shakePhase += dt;

    const isSmiling = frame.face.visible && frame.face.smiling === true;
    state.smiling = isSmiling;

    if (isSmiling) {
      state.notSmilingFor = 0;
      state.totalSmileSec += dt;
      state.progress = clamp01(state.progress + FILL_RATE * dt);
    } else {
      state.notSmilingFor += dt;
      if (state.notSmilingFor > BREAK_GRACE_SEC) {
        // Hard reset once the player has been not-smiling past the grace window.
        state.progress = 0;
      } else {
        // Slow drain while inside the grace window.
        state.progress = clamp01(state.progress - DRAIN_RATE * dt);
      }
    }

    if (state.progress >= 1) {
      state.progress = 1;
      state.won = true;
      state.finished = true;
      state.gameCtx?.audio.win();
      return;
    }

    if (state.elapsed >= DURATION_SEC) {
      state.finished = true;
      state.won = false;
      state.gameCtx?.audio.lose();
    }
  };

  const render = (g: CanvasRenderingContext2D): void => {
    if (state.gameCtx == null) return;
    const { canvas } = state.gameCtx;
    const w = canvas.width;
    const h = canvas.height;
    const cx = w / 2;
    const cy = h / 2;

    // Dark background.
    g.fillStyle = '#0d0e14';
    g.fillRect(0, 0, w, h);

    const progress = state.progress;

    // Radial progress ring.
    const ringRadius = Math.min(w, h) * 0.28;
    const ringThickness = Math.max(10, Math.min(w, h) * 0.035);

    // Outer gray track.
    g.lineWidth = ringThickness;
    g.lineCap = 'round';
    g.strokeStyle = '#23252f';
    g.beginPath();
    g.arc(cx, cy, ringRadius, 0, Math.PI * 2);
    g.stroke();

    // Filled arc — yellow to green as progress grows.
    const yellow: [number, number, number] = [255, 214, 51];
    const green: [number, number, number] = [80, 220, 120];
    g.strokeStyle = lerpColor(yellow, green, progress);
    g.beginPath();
    const start = -Math.PI / 2;
    const end = start + progress * Math.PI * 2;
    g.arc(cx, cy, ringRadius, start, end);
    g.stroke();

    // Percent number inside ring.
    g.fillStyle = '#ffffff';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `bold ${Math.round(h * 0.07)}px system-ui, sans-serif`;
    g.fillText(`${Math.round(progress * 100)}%`, cx, cy - h * 0.01);

    // Face state icon below percent.
    const iconR = Math.max(14, h * 0.04);
    const iconCx = cx;
    const iconCy = cy + h * 0.07;
    g.fillStyle = state.smiling ? '#ffd633' : '#7d8298';
    g.beginPath();
    g.arc(iconCx, iconCy, iconR, 0, Math.PI * 2);
    g.fill();
    // Eyes.
    g.fillStyle = '#0d0e14';
    const eyeOff = iconR * 0.4;
    const eyeR = iconR * 0.13;
    g.beginPath();
    g.arc(iconCx - eyeOff, iconCy - iconR * 0.2, eyeR, 0, Math.PI * 2);
    g.arc(iconCx + eyeOff, iconCy - iconR * 0.2, eyeR, 0, Math.PI * 2);
    g.fill();
    // Mouth.
    g.strokeStyle = '#0d0e14';
    g.lineWidth = Math.max(2, iconR * 0.13);
    g.lineCap = 'round';
    g.beginPath();
    if (state.smiling) {
      g.arc(iconCx, iconCy + iconR * 0.05, iconR * 0.45, 0, Math.PI);
    } else {
      g.moveTo(iconCx - iconR * 0.4, iconCy + iconR * 0.35);
      g.lineTo(iconCx + iconR * 0.4, iconCy + iconR * 0.35);
    }
    g.stroke();

    // Center title — "DON'T LAUGH!" — above the ring, subtle shake when progress > 50%.
    g.save();
    let shakeX = 0;
    let shakeY = 0;
    if (progress > 0.5) {
      const amp = (progress - 0.5) * 12; // up to ~6 px at 100%
      shakeX = Math.sin(state.shakePhase * 38) * amp;
      shakeY = Math.cos(state.shakePhase * 31) * amp * 0.6;
    }
    g.translate(shakeX, shakeY);
    g.fillStyle = '#ffffff';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `bold ${Math.round(h * 0.08)}px system-ui, sans-serif`;
    g.fillText("DON'T LAUGH!", cx, cy - ringRadius - h * 0.09);
    g.restore();

    // HUD bottom: time remaining.
    const remaining = Math.max(0, DURATION_SEC - state.elapsed);
    g.fillStyle = '#9aa0bf';
    g.textAlign = 'center';
    g.textBaseline = 'alphabetic';
    g.font = `${Math.round(h * 0.04)}px system-ui, sans-serif`;
    g.fillText(`${remaining.toFixed(1)}s`, cx, h - h * 0.05);
  };

  const end = (): MicrogameResult => {
    const score = Math.round(state.progress * 100);
    return {
      microgame: '05-smile-hold',
      won: state.won,
      score,
      detail: {
        secondsHeld: Number(state.totalSmileSec.toFixed(2)),
      },
    };
  };

  return {
    name: '05-smile-hold',
    durationSec: DURATION_SEC,
    requires: { face: true },
    init,
    tick,
    render,
    end,
  };
})();
